import * as React from "react";
import { Dropdown } from "antd";
import classnames from "classnames";
import { ColumnProps } from "antd/lib/table";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { dayjs } from "Config/dayjs";
import { FormatData } from "utils/FormatData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores/ProjectsStore";
import { fileDetailsStore } from "../FileDetailsStore";
import { API } from "General/API";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { download } from "utils/download";
import { wopiViewerStore } from "Stores/WOPIViewerStore";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { OnlineViewerCompare, OnlineViewerCompareProps } from "./OnlineViewerCompare";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { nanoid } from "nanoid";
import { Table } from "Components/General/Table";
import { apryseViewerStore } from "Stores/PdfViewerStore/ApryseViewerStore";
import { formatUsername } from "utils/formatUsername";
// import { Index } from 'react-virtualized';

interface VersionsProps extends WithTranslation, WithModal {}

interface VersionsState {
  data: API_FileDataEntity[];
  isDeleted: boolean;
  currentIndex: number;
  loading: boolean;
}

export const Versions = withModal(
  withTranslation(I18N_NS.PROJECT)(
    class extends React.Component<VersionsProps, VersionsState> {
      constructor(props: VersionsProps) {
        super(props);

        this.state = {
          data: [],
          isDeleted: false,
          currentIndex: null,
          loading: false,
        };
      }

      componentDidMount() {
        this.handleFetch();
      }

      handleDownload = async (record: API_FileDataEntity) => {
        try {
          let url: string;
          if (record.state === "ARCHIVED" || record.state === "DELETED") {
            url = API_URL.FILES.getArchiveFileById(projectsDataStore.projectId, record.id, record.archivId);
          } else {
            url = API_URL.FILES.getFileById(projectsDataStore.projectId, record.id);
          }
          const file: File = await API.getBlob(url);
          download(file, file.name, file.type);
        } catch (error) {
          throw error;
        }
      };

      renderDropdownActionButtons = (text, record: API_FileDataEntity) => {
        const { t, showModal } = this.props;

        // https://pmgredesign.atlassian.net/browse/DEV-3893?focusedCommentId=33416 disable actions for deleted files
        // if (this.state.isDeleted) {
        //   return null;
        // }

        const dropdownActions: { label: string; onClick: (record?: API_FileDataEntity) => void; icon: IconName }[] = [];

        const { viewers } = record;
        if (record.fileActions && record.fileActions.download) {
          dropdownActions.push({
            label: t("Download"),
            onClick: this.handleDownload,
            icon: "download",
          });
        }
        if (viewers) {
          if (viewers.onlineViewer && record.dwgDxfDatei) {
            dropdownActions.push({
              label: t("View with Online Viewer"),
              onClick: () =>
                onlineViewerStore.fetch(record.id, {
                  archiveFileId: record.archivId && record.archivId !== 0 ? record.archivId : undefined,
                  fileName: record.name,
                }),
              icon: "eye",
            });
          } else if (viewers.webViewer) {
            dropdownActions.push({
              label: t("View with WebViewer"),
              onClick: () =>
                apryseViewerStore.fetch(record.id, {
                  fileName: record.name,
                  canDownload: record.fileActions.download || record.fileActions.downloadWithWatermark,
                  archivedFileId: record.archivId && record.archivId !== 0 ? record.archivId : undefined,
                }),
              icon: "eye",
            });
          } else if (viewers.easyViewer) {
            dropdownActions.push({
              label: t("View with Easy Viewer"),
              onClick: () =>
                pdfViewerStore.fetch(record.id, {
                  fileName: record.name,
                  canDownload: record.fileActions.download || record.fileActions.downloadWithWatermark,
                  archivedFileId: record.archivId && record.archivId !== 0 ? record.archivId : undefined,
                }),
              icon: "eye",
            });
          } else if (viewers.office365) {
            const isArchived = record.archivId && record.archivId !== 0;
            dropdownActions.push({
              label: t("View with Office Online"),
              onClick: () =>
                wopiViewerStore.fetch(isArchived ? record.archivId : record.id, {
                  fileName: record.name,
                  archived: isArchived,
                }),
              icon: "eye",
            });
            if (viewers.office365Edit) {
              const isArchived = record.archivId && record.archivId !== 0;
              dropdownActions.push({
                label: t("Edit with Office Online"),
                onClick: () =>
                  wopiViewerStore.fetch(isArchived ? record.archivId : record.id, {
                    fileName: record.name,
                    archived: isArchived,
                    edit: viewers.office365Edit,
                  }),
                icon: "edit",
              });
            }
          } else if (viewers.onlineViewer) {
            dropdownActions.push({
              label: t("View with Online Viewer"),
              onClick: () =>
                onlineViewerStore.fetch(record.id, {
                  archiveFileId: record.archivId && record.archivId !== 0 ? record.archivId : undefined,
                  fileName: record.name,
                }),
              icon: "eye",
            });
          }
          if (viewers.onlineViewerCompare) {
            dropdownActions.push({
              label: t("Compare with..."),
              onClick: () =>
                showModal<OnlineViewerCompareProps>(OnlineViewerCompare, {
                  modalProps: {
                    ...modalProps,
                    width: "100%",
                    title: t("Compare with..."),
                    className: "full-height-modal",
                    style: { maxWidth: `calc(100vw - 40px)` },
                  },
                  componentProps: {
                    files: this.state.data,
                    fileToCompare: record,
                  },
                }),
              icon: "not-equal",
            });
          }
        }

        if (!dropdownActions.length) {
          return null;
        }

        const onClick = ({ key }) => {
          dropdownActions[key].onClick(record);
        };

        return (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: dropdownActions.map((item, idx) => ({
                key: idx,
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", item.icon] as IconProp} fixedWidth={true} />
                    {t(item.label)}
                  </>
                ),
              })),
              onClick: onClick,
              className: "documents-actions",
            }}
            placement="bottomRight"
          >
            <span>
              <FontAwesomeIcon
                className="detail-action-icon"
                style={{ color: "#818288" }}
                icon={["far", "ellipsis-v"]}
                fixedWidth={true}
              />
            </span>
          </Dropdown>
        );
      };

      async handleFetch() {
        try {
          this.setState({ loading: true });
          const response = await API.getData<API_FileDataEntity[]>(
            API_URL.FILES.getVersionsInfo_1(projectsDataStore.projectId, fileDetailsStore.fileId)
          );
          const isDeleted = response.some((item) => item.state === "DELETED");
          this.setState({ data: response.map((item) => ({ ...item, uid: nanoid() })), isDeleted });
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
        }
      }

      // rowClassName = (record: API_FileDataEntity) => {
      //   if (this.state.isDeleted) {
      //     return 'disabledCell';
      //   }
      //   return undefined;
      // }
      onRowClick = (event, index) => {
        this.setState({ currentIndex: index });
      };
      getRowClassName = (record: API_FileDataEntity, index) => {
        if (record.deleted) {
          return "disabledCell";
        }
        return classnames({
          highlighted: index === this.state.currentIndex,
        });
      };

      render() {
        const { t } = this.props;
        const { loading, data } = this.state;

        const columns: ColumnProps<API_FileDataEntity>[] = [
          {
            dataIndex: "name",
            title: t("Filename"),
            width: 200,
          },
          {
            dataIndex: "description",
            title: t("Description"),
            width: 150,
          },
          {
            dataIndex: "index",
            title: t("Index"),
            width: 150,
          },
          {
            dataIndex: "ownerUserId",
            title: t("Owner"),
            width: 150,
            render: (text, record) =>
              formatUsername({ firstName: record.ownerFirstName, surname: record.ownerSurname }),
          },
          {
            dataIndex: "size",
            title: t("Size"),
            render: (text) => FormatData.formatBytes(+text, 2),
            width: 100,
          },
          {
            dataIndex: "created",
            title: t("Last change"),
            render: (text) => dayjs(+text).format("lll"),
            sorter: (a, b) => +a.created - +b.created,
            width: 170,
          },
          {
            dataIndex: "id",
            render: this.renderDropdownActionButtons,
            width: 70,
          },
          {
            dataIndex: "fileState",
            width: 20,
            render: (cellData) => (
              <div style={{ padding: 0, width: 35 }} className={`status-marker marker-${cellData}`} />
            ),
          },
        ];

        return (
          <Table<API_FileDataEntity>
            name="fileVersions"
            className={"antd-versions-table"}
            sortDirections={["ascend", "descend"]}
            rowKey="uid"
            loading={loading}
            dataSource={data}
            columns={columns}
            rowClassName={this.getRowClassName}
            pagination={{ hideOnSinglePage: true }}
            onRow={(record, rowIndex) => {
              return {
                onClick: (e) => this.onRowClick(e, rowIndex),
              };
            }}
          />
        );
      }
    }
  )
);
